import { useState } from 'react';
import { lookupImei, type ImeiLookupResult } from '@/lib/api/imei.js';
import { parseApiError } from '@/lib/api/errors.js';

type DeviceInfo = {
  make: string;
  model: string;
  storage: string;
  color: string;
  carrier: string;
};

type Props = {
  value: string;
  onChange: (imei: string) => void;
  onApplyDevice: (info: DeviceInfo) => void;
};

const BLACKLISTED_STATUSES = new Set(['stolen', 'lost', 'blocked']);

export function ImeiLookupPanel({ value, onChange, onApplyDevice }: Props) {
  const [result, setResult] = useState<ImeiLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);

  async function doLookup() {
    if (!/^\d{14,15}$/.test(value)) {
      setError('Enter a 14–15 digit IMEI to look up');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await lookupImei(value);
      setResult(res);
      if (res.error) setError(res.error);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }

  function handleImeiChange(raw: string) {
    const next = raw.replace(/\D/g, '').slice(0, 15);
    onChange(next);
    // Clear prior lookup when staff edits the number — typing alone never calls the API
    if (result || error) {
      setResult(null);
      setError(null);
    }
  }

  function handleApply() {
    if (!result?.deviceInfo) return;
    const { status } = result.blacklistStatus ?? {};
    if (status && BLACKLISTED_STATUSES.has(status)) {
      setShowBlacklistModal(true);
      return;
    }
    applyDevice();
  }

  function applyDevice() {
    if (!result?.deviceInfo) return;
    onApplyDevice({
      make: result.deviceInfo.make,
      model: result.deviceInfo.model,
      storage: result.deviceInfo.storage,
      color: result.deviceInfo.color,
      carrier: result.deviceInfo.carrier,
    });
    setShowBlacklistModal(false);
  }

  const blacklistStatus = result?.blacklistStatus?.status;
  const isBlacklisted = blacklistStatus && BLACKLISTED_STATUSES.has(blacklistStatus);
  const canLookup = /^\d{14,15}$/.test(value);

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          maxLength={15}
          className="flex-1 rounded-xl border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          placeholder="IMEI (optional)"
          value={value}
          onChange={(e) => handleImeiChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canLookup && !loading) void doLookup();
            }
          }}
        />
        <button
          type="button"
          disabled={loading || !canLookup}
          title={canLookup ? 'Look up make/model from IMEI' : 'Enter a 14–15 digit IMEI first'}
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
          onClick={() => void doLookup()}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Looking up…
            </span>
          ) : (
            'Lookup'
          )}
        </button>
      </div>
      <p className="text-xs text-slate-500">
        IMEI is saved with the device as you type. Lookup is optional if you want make/model filled in.
      </p>

      {error ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {error}
        </div>
      ) : null}

      {result && result.valid && result.deviceInfo ? (
        <div
          className={[
            'rounded-xl border p-4',
            isBlacklisted ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50',
          ].join(' ')}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold text-slate-950">
                {[result.deviceInfo.make, result.deviceInfo.model].filter(Boolean).join(' ')}
              </p>
              {(() => {
                const details = [
                  result.deviceInfo.storage,
                  result.deviceInfo.color,
                  result.deviceInfo.carrier,
                  result.deviceInfo.modelNumber,
                  result.deviceInfo.releaseYear,
                ].filter((v) => v && v.toLowerCase() !== 'unknown');
                return details.length ? (
                  <p className="text-sm text-slate-600">{details.join(' · ')}</p>
                ) : null;
              })()}
              {result.blacklistStatus ? (
                <p
                  className={[
                    'text-xs font-semibold uppercase',
                    isBlacklisted
                      ? 'text-red-700'
                      : blacklistStatus === 'clean'
                        ? 'text-emerald-700'
                        : 'text-slate-500',
                  ].join(' ')}
                >
                  {isBlacklisted
                    ? `⚠ ${result.blacklistStatus.status.toUpperCase()}`
                    : blacklistStatus === 'clean'
                      ? '✓ Clean'
                      : 'Blacklist: not reported'}
                </p>
              ) : null}
              {result.warrantyInfo ? (
                <p className="text-xs text-slate-500">
                  Warranty: {result.warrantyInfo.status}
                  {result.warrantyInfo.expiresAt
                    ? ` (expires ${new Date(result.warrantyInfo.expiresAt).toLocaleDateString()})`
                    : ''}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="shrink-0 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-[#164477]"
              onClick={handleApply}
            >
              Use This Device
            </button>
          </div>
        </div>
      ) : null}

      {result && !result.valid && !error ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          IMEI not recognized or lookup not configured. You can still enter make and model manually.
        </div>
      ) : null}

      {showBlacklistModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-red-700">
              ⚠ Device Flagged — {result?.blacklistStatus?.status?.toUpperCase()}
            </h3>
            <p className="mt-3 text-sm text-slate-700">
              This device ({result?.deviceInfo?.make} {result?.deviceInfo?.model}) has been reported
              as <strong>{result?.blacklistStatus?.status}</strong>
              {result?.blacklistStatus?.reportedDate
                ? ` on ${new Date(result.blacklistStatus.reportedDate).toLocaleDateString()}`
                : ''}
              {result?.blacklistStatus?.carrier
                ? ` by ${result.blacklistStatus.carrier}`
                : ''}
              . Proceed only if you have verified the customer&apos;s documentation.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => setShowBlacklistModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                onClick={applyDevice}
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
